import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useAuth } from "@/lib/auth";
import { hasMinRole, type SystemRole } from "@/lib/rbac";
import { GUIDES, type Guide, type GuideId } from "@/lib/guides";
import { useConnections } from "@fastn-ai-team/connector-ui";
import { useWorkflows } from "@/hooks/use-workflows";
import { useWidgets } from "@/hooks/use-widgets";

// ─── Manually-completed guides ────────────────────────────────────────────────
// Most guides are marked done by observing real account state (a connector
// exists, a workflow exists). "Connect fastn to your favourite agent" happens
// inside that agent, where we cannot observe it, and any guide can be dismissed
// by hand — both go
// to localStorage, scoped per org so switching accounts does not carry progress
// across. A module-level store keeps the home card and /guides in sync when one
// of them writes, and a `storage` listener does the same across tabs.

/**
 * Roles offered guides at all. Mirrors the nav entry in lib/rbac.ts and the
 * route gate in App.tsx — all three must agree or a link leads somewhere the
 * user is denied.
 */
const GUIDE_ROLES: SystemRole[] = ["owner", "admin", "developer"];

/** Why a write did not land, so the UI can say something true about it. */
export type GuideSaveResult = "ok" | "no-org" | "storage-blocked";

/**
 * Null when there is no org to scope to.
 *
 * `userContext.orgId` is `''` — not undefined — when the JWT carries no
 * `org_id` claim, and it is backfilled from memberships a moment later. A
 * fallback key would therefore be shared by every org-less session on the
 * machine, and anything written under it would vanish the instant the backfill
 * landed. Storage is skipped entirely until a real org is known.
 *
 * Keyed by user *and* org: this is one person's checklist, not the org's. It
 * is also browser-local, so a manual tick does not follow the user to another
 * device — acceptable for a nudge, but the reason the home card decides
 * "set up" from observed account state rather than from these ticks. Moving
 * this to a server-side user preference is the real fix if it needs to travel.
 */
function storageKey(userId: string | undefined, orgId: string | undefined): string | null {
  return orgId ? `fastn-guides-completed:${orgId}:${userId ?? "anon"}` : null;
}

function read(userId: string | undefined, orgId: string | undefined): string {
  const key = storageKey(userId, orgId);
  if (!key) return "[]";
  try {
    return localStorage.getItem(key) ?? "[]";
  } catch {
    return "[]";
  }
}

/**
 * Ids that have been renamed, mapped old to new.
 *
 * These ticks are browser-local, so a rename with no mapping silently un-ticks
 * the guide for everyone who had already done it, and the home card starts
 * nagging again about work they finished. Rewriting on read rather than
 * migrating the stored value keeps it lazy and idempotent — the new id is
 * written back the next time anything else is ticked.
 */
const RENAMED_GUIDE_IDS: Record<string, GuideId> = {
  "connect-claude": "connect-mcp",
};

function parse(raw: string): GuideId[] {
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    const ids = value.map((id) =>
      typeof id === "string" && id in RENAMED_GUIDE_IDS ? RENAMED_GUIDE_IDS[id] : id,
    ) as GuideId[];
    // A user who ticked the old id and then the new one would otherwise carry
    // the same guide twice, which double-counts in the completion total.
    return [...new Set(ids)];
  } catch {
    return [];
  }
}

const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // Another tab writing the same key must not be silently overwritten later.
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
}

function write(
  userId: string | undefined,
  orgId: string | undefined,
  ids: GuideId[],
): GuideSaveResult {
  const key = storageKey(userId, orgId);
  // Not the browser's fault — the org simply has not resolved yet.
  if (!key) return "no-org";
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // Private browsing, a blocked-storage policy, or quota exhaustion.
    return "storage-blocked";
  }
  listeners.forEach((l) => l());
  return "ok";
}

export interface GuideProgress {
  guide: Guide;
  complete: boolean;
  /** True when completion was observed from account state rather than clicked. */
  auto: boolean;
  /**
   * True when the query backing auto-detection failed, so we genuinely do not
   * know whether this guide is done. Distinct from "observed as not done" and
   * from "not checked yet" — see `isLoading`.
   */
  unknown: boolean;
}

export interface GuidesResult {
  guides: GuideProgress[];
  completedCount: number;
  total: number;
  /** True until the queries backing auto-completion have resolved. */
  isLoading: boolean;
  /** True when a backing query failed — completion state is not trustworthy. */
  isError: boolean;
  /**
   * True when there is no org to evaluate guides against, so nothing can be
   * checked and nothing can be saved. A distinct state from loading and error:
   * it never resolves on its own, so the UI must say so rather than spin.
   */
  isUnavailable: boolean;
  markComplete: (id: GuideId) => GuideSaveResult;
  markIncomplete: (id: GuideId) => GuideSaveResult;
}

export function useGuides(): GuidesResult {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;
  const userId = userContext?.sub;
  const role = (userContext?.role ?? "end_user") as SystemRole;
  const hasOrg = !!orgId;

  // Explicit list, kept identical to the Guides nav entry and route gate. Using
  // the hierarchy let platform_admin inherit the guides while the route denied
  // it, so the home checklist rendered links onto a blank page.
  const roleGuides = useMemo(
    () => (GUIDE_ROLES.includes(role) ? GUIDES.filter((g) => hasMinRole(role, g.minRole)) : []),
    [role],
  );

  // Each query is gated on whether a guide this role can see actually reads it,
  // so an operator (whose only guide is connect-first-app) does not pay for
  // /workflows and /widgets, and the MCP guide — detected from a constant —
  // fetches nothing at all. Without an org none of them can run.
  const needs = (id: GuideId) => hasOrg && roleGuides.some((g) => g.id === id);

  // useSyncExternalStore requires a stable snapshot reference, so the raw JSON
  // string is the snapshot and parsing happens in a memo below.
  const raw = useSyncExternalStore(
    subscribe,
    useCallback(() => read(userId, orgId), [userId, orgId]),
    useCallback(() => "[]", []),
  );

  const manual = useMemo(() => parse(raw), [raw]);

  // Authorized connections, NOT the connector catalog: /connectors also returns
  // the shared platform library and inherited ancestor connectors, so a brand
  // new org that has connected nothing still gets a non-empty list — and the
  // guide would tick itself before the user had done anything.
  const connectionsQuery = useConnections({ enabled: needs("connect-first-app") });
  const workflowsQuery = useWorkflows(undefined, { enabled: needs("build-first-workflow") });
  const widgetsQuery = useWidgets(undefined, { enabled: needs("embed-widget") });

  const queries = [connectionsQuery, workflowsQuery, widgetsQuery];
  // Only a query that is genuinely in flight counts as loading; a disabled one
  // never resolves.
  const isLoading = queries.some((q) => q.isPending && q.fetchStatus === "fetching");

  // Three outcomes, deliberately distinct: a boolean is an answer, `null` is a
  // failed lookup, `undefined` is a lookup that has not happened yet. Folding
  // the last two together made a disabled query render as "check failed"
  // forever for any session whose org never resolves.
  const detected = useMemo<Record<GuideId, boolean | null | undefined>>(() => {
    const seen = (q: {
      isSuccess: boolean;
      isError: boolean;
      data?: unknown[];
    }): boolean | null | undefined => {
      if (q.isSuccess) return (q.data?.length ?? 0) > 0;
      if (q.isError) return null;
      return undefined;
    };
    return {
      "connect-first-app": connectionsQuery.isSuccess
        ? (connectionsQuery.data?.connections?.length ?? 0) > 0
        : connectionsQuery.isError
          ? null
          : undefined,
      "build-first-workflow": seen(workflowsQuery),
      "embed-widget": seen(widgetsQuery),
      // Adding an MCP server happens in the client, not here — never
      // auto-detected, which is a known answer rather than a failed lookup.
      "connect-mcp": false,
    };
    // Depends on primitives: React Query returns a fresh tracked proxy each
    // render, so depending on the result objects meant this memo (and the
    // `guides` memo below it) never hit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    connectionsQuery.isSuccess, connectionsQuery.isError, connectionsQuery.data?.connections?.length,
    workflowsQuery.isSuccess, workflowsQuery.isError, workflowsQuery.data?.length,
    widgetsQuery.isSuccess, widgetsQuery.isError, widgetsQuery.data?.length,
  ]);

  const guides = useMemo<GuideProgress[]>(
    () =>
      roleGuides.map((guide) => {
        const auto = detected[guide.id];
        const ticked = manual.includes(guide.id);
        return {
          guide,
          auto: auto === true,
          complete: auto === true || ticked,
          unknown: auto === null && !ticked,
        };
      }),
    [roleGuides, detected, manual],
  );

  const markComplete = useCallback(
    (id: GuideId) => {
      // Re-read rather than trusting the render's snapshot, so a write from
      // another tab in the meantime is merged instead of discarded.
      const current = parse(read(userId, orgId));
      if (current.includes(id)) return "ok" as const;
      return write(userId, orgId, [...current, id]);
    },
    [userId, orgId],
  );

  const markIncomplete = useCallback(
    (id: GuideId) => {
      const current = parse(read(userId, orgId));
      if (!current.includes(id)) return "ok" as const;
      return write(
        userId,
        orgId,
        current.filter((g) => g !== id),
      );
    },
    [userId, orgId],
  );

  return {
    guides,
    completedCount: guides.filter((g) => g.complete).length,
    total: guides.length,
    isLoading,
    isError: guides.some((g) => g.unknown),
    isUnavailable: !hasOrg && roleGuides.length > 0,
    markComplete,
    markIncomplete,
  };
}
